import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery, useTheme
} from '@mui/material';
import { Photo, Movie, Storage as StorageIcon, Person, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { fadeInUp, GlassmorphicCard } from '../styles/sharedStyles';

// Helper function to get color based on storage share percentage
const getShareColor = (share) => {
  if (share < 50) return '#4caf50'; // Green - safe
  if (share < 70) return '#ffeb3b'; // Yellow - warning
  if (share < 85) return '#ff9800'; // Orange - caution
  return '#f44336'; // Red - danger
};

const SummaryCard = styled(GlassmorphicCard)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

// Compact card for mobile horizontal scroll (Photos, Videos, Storage)
const CompactSummaryCard = styled(GlassmorphicCard)(({ theme }) => ({
  padding: theme.spacing(1.25),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: 72,
  transition: 'all 0.2s ease',
  '&:hover': {
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: 48,
  height: 48,
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.4}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1.5),
  background: `rgba(${color}, 0.1)`,
  color: `rgb(${color})`,
  border: `1px solid rgba(${color}, 0.2)`,
  boxShadow: `0 8px 16px rgba(${color}, 0.1)`,
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
    borderRadius: `${(theme.custom?.cardRounding || 28) * 0.35}px`,
    marginRight: theme.spacing(1),
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: theme.palette.background.paper,
  backdropFilter: `blur(${theme.custom?.glassBlur || 16}px)`,
  borderRadius: `${theme.custom?.cardRounding || 28}px`,
  border: '1px solid rgba(255, 255, 255, 0.05)',
  marginTop: theme.spacing(4),
  overflow: 'hidden',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.8)',
    padding: theme.spacing(2, 3),
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: `${theme.palette.primary.main}1a`,
    fontWeight: 700,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    letterSpacing: '1px',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
  },
}));

const ShareBar = styled(Box)(({ theme, share, color }) => ({
  width: '100%',
  height: 6,
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: 3,
  overflow: 'hidden',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${share}%`,
    background: color,
    borderRadius: 3,
  }
}));

const SessionInfo = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(1),
  background: `${theme.palette.primary.main}0d`,
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.4}px`,
  border: `1px solid ${theme.palette.primary.main}1a`,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    background: `${theme.palette.primary.main}1a`,
    borderColor: `${theme.palette.primary.main}33`,
  }
}));

const StatusBadge = styled(Box)(({ theme, online }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.3}px`,
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  backgroundColor: online ? `${theme.palette.success.main}1a` : 'rgba(255, 255, 255, 0.05)',
  color: online ? theme.palette.success.main : 'rgba(255, 255, 255, 0.5)',
  border: online ? `1px solid ${theme.palette.success.main}33` : '1px solid rgba(255, 255, 255, 0.1)',
  width: 'fit-content',
}));

// Mobile: horizontal scroll row for the 3 summary cards (Photos, Videos, Storage)
const SummaryScrollRow = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    gap: theme.spacing(1.5),
    overflowX: 'auto',
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(-2),
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > *': {
      flex: '0 0 42%',
      scrollSnapAlign: 'start',
      minWidth: '120px',
      maxWidth: '160px',
    },
  },
}));

// Mobile: horizontal scroll row for user cards — same left margin as disk pools (StatusCard)
const UserCardsScrollRow = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    gap: theme.spacing(2),
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: theme.spacing(0, -2),
    marginLeft: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > *': {
      flex: '0 0 85%',
      scrollSnapAlign: 'start',
      minWidth: '280px',
    },
  },
}));

const IMMICH_USER_MAPPING = {
  'Adam': 'a92fdf0d-6175-4e4b-b484-ddcb571a6102',
  'Piotrek': 'f7bd4e8a-fe90-486d-9f67-040b90baa912',
  'Marysia': '8a867261-151b-4ffe-906c-a45e28633a2f',
  'Teresa Pawlowska': 'ab0dbf5e-f7a3-45d8-bece-6b68fafd90a3',
  'Zuzanna Pawlowska': 'b1d5895b-9a8b-445b-baa9-87d4fbc3dbc7'
};

const ImmichStatistics = ({ apiBaseUrl, token, onSessionStats, isTrueNASOffline }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [userSessions, setUserSessions] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const toggleExpand = (userName) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userName]: !prev[userName]
    }));
  };

  const fetchStatistics = useCallback(async () => {
    if (isTrueNASOffline) {
      console.log('⚠️ Skipping Immich statistics fetch: TrueNAS is offline');
      setData(null);
      setUserSessions({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/immich/statistics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setData(response.data);
      
      // Fetch sessions for all users in parallel
      const userEntries = Object.entries(IMMICH_USER_MAPPING);
      const sessionRequests = userEntries.map(async ([name, userId]) => {
        try {
          const sessionsResponse = await axios.get(`${apiBaseUrl}/api/immich/users/${userId}/sessions`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          // Sort by updatedAt descending and keep top 3
          const sortedSessions = (sessionsResponse.data || [])
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
            .slice(0, 3);
            
          return { name, sessions: sortedSessions };
        } catch (err) {
          console.error(`Error fetching sessions for ${name}:`, err);
          return { name, sessions: [] };
        }
      });

      const allSessionsResults = await Promise.all(sessionRequests);
      const sessionsMap = {};
      allSessionsResults.forEach(res => {
        sessionsMap[res.name] = res.sessions;
      });

      setUserSessions(sessionsMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching Immich statistics:', err);
      setError(err.response?.data?.detail || 'Failed to fetch Immich statistics. Ensure the backend is configured with IMMICH_API_KEY and IMMICH_BASE_URL.');
      setUserSessions({});
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token, isTrueNASOffline]);

  useEffect(() => {
    // Only fetch if data is null or if we just came online
    if (!data && !isTrueNASOffline) {
      fetchStatistics();
    }
  }, [fetchStatistics, data, isTrueNASOffline]);

  useEffect(() => {
    if (!onSessionStats) return;
    const now = Date.now();
    let total = 0;
    let online = 0;
    Object.values(userSessions).forEach(sessions => {
      if (!Array.isArray(sessions)) return;
      total += sessions.length;
      sessions.forEach(session => {
        const updatedAt = session?.updatedAt;
        if (updatedAt && (now - new Date(updatedAt)) < 10 * 60 * 1000) {
          online += 1;
        }
      });
    });
    onSessionStats({ total, online });
  }, [userSessions, onSessionStats]);

  const formatBytes = (bytes) => {
    return (bytes / 1073741824).toFixed(2);
  };

  const sortedUsageByUser = useMemo(() => {
    if (!data?.usageByUser) return [];
    return [...data.usageByUser].sort((a, b) => (b.usage || 0) - (a.usage || 0));
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          borderRadius: '16px', 
          mt: 2,
          backgroundColor: 'rgba(211, 47, 47, 0.1)',
          color: '#ff8a80',
          border: '1px solid rgba(211, 47, 47, 0.2)',
          '& .MuiAlert-icon': { color: '#ff8a80' }
        }}
      >
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <Box sx={{ animation: `${fadeInUp} 0.6s ease-out` }}>
      {/* Desktop: 3-column grid for summary cards */}
      <Grid container spacing={3} sx={{ display: { xs: 'none', sm: 'flex' } }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard>
            <IconWrapper color="0, 212, 255">
              <Photo fontSize="large" />
            </IconWrapper>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.65rem' }}>Total Photos</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>{data.photos.toLocaleString()}</Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard>
            <IconWrapper color="255, 64, 129">
              <Movie fontSize="large" />
            </IconWrapper>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.65rem' }}>Total Videos</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>{data.videos.toLocaleString()}</Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard>
            <IconWrapper color="0, 255, 136">
              <StorageIcon fontSize="large" />
            </IconWrapper>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.65rem' }}>Server Usage</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>{formatBytes(data.usage)} <Typography component="span" variant="body1" sx={{ opacity: 0.7 }}>GB</Typography></Typography>
            </Box>
          </SummaryCard>
        </Grid>
      </Grid>

      {/* Mobile: horizontal scroll — compact Photos, Videos, Storage */}
      <SummaryScrollRow>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0, 212, 255, 0.15)', color: '#00d4ff', mb: 0.5 }}>
            <Photo sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Photos</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{data.photos.toLocaleString()}</Typography>
        </CompactSummaryCard>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255, 64, 129, 0.15)', color: '#ff4081', mb: 0.5 }}>
            <Movie sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Videos</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{data.videos.toLocaleString()}</Typography>
        </CompactSummaryCard>
        <CompactSummaryCard>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0, 255, 136, 0.15)', color: '#00ff88', mb: 0.5 }}>
            <StorageIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.6rem' }}>Storage</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{formatBytes(data.usage)} GB</Typography>
        </CompactSummaryCard>
      </SummaryScrollRow>

      {isMobile ? (
        <UserCardsScrollRow sx={{ mt: 3 }}>
          {sortedUsageByUser.map((user) => {
            const share = data.usage > 0 ? ((user.usage / data.usage) * 100).toFixed(2) : "0.00";
            return (
              <GlassmorphicCard key={user.userName} sx={{ p: 2, borderRadius: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '12px', 
                    background: 'rgba(0, 212, 255, 0.1)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 2,
                    color: '#00d4ff'
                  }}>
                    <Person />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>{user.userName}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {user.photos.toLocaleString()} Photos • {user.videos.toLocaleString()} Videos
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#00ff88' }}>
                      {formatBytes(user.usage)} GB
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {share}% share
                    </Typography>
                  </Box>
                </Box>

                <ShareBar share={share} color={getShareColor(share)} sx={{ mt: 1.5, mb: 1.5 }} />

                {/* Mobile Sessions Info */}
                {userSessions[user.userName] && userSessions[user.userName].length > 0 && (
                  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box 
                      onClick={() => toggleExpand(user.userName)}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover .MuiTypography-root': { color: 'rgba(255, 255, 255, 0.6)' }
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px', transition: 'color 0.2s' }}>
                        {expandedUsers[user.userName] ? 'Recent Sessions' : 'Last Session'}
                      </Typography>
                      {userSessions[user.userName].length > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.3)' }}>
                          {expandedUsers[user.userName] ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </Box>
                      )}
                    </Box>
                    
                    {(expandedUsers[user.userName] 
                      ? userSessions[user.userName] 
                      : [userSessions[user.userName][0]]
                    ).map((session, idx) => {
                      const isOnline = (Date.now() - new Date(session.updatedAt)) < 10 * 60 * 1000;
                      const deviceName = session.appVersion 
                        ? `${session.deviceOS} (App v${session.appVersion})`
                        : `${session.deviceOS} (${session.deviceType})`;
                        
                      return (
                        <SessionInfo key={idx} onClick={() => toggleExpand(user.userName)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <StatusBadge online={isOnline}>
                              {isOnline ? 'ONLINE' : 'Offline'}
                            </StatusBadge>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.65rem' }}>
                              {new Date(session.updatedAt).toLocaleString()}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, fontSize: '0.7rem' }}>
                            {deviceName}
                          </Typography>
                        </SessionInfo>
                      );
                    })}
                  </Box>
                )}
                
              </GlassmorphicCard>
            );
          })}
        </UserCardsScrollRow>
      ) : (
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell align="right">Photos</TableCell>
                <TableCell align="right">Videos</TableCell>
                <TableCell align="right">Storage (GB)</TableCell>
                <TableCell align="right" sx={{ width: '200px' }}>Share (%)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsageByUser.map((user) => {
                const share = data.usage > 0 ? ((user.usage / data.usage) * 100).toFixed(2) : "0.00";
                return (
                  <TableRow key={user.userName}>
                    <TableCell component="th" scope="row">
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: userSessions[user.userName]?.length > 0 ? 1.5 : 0 }}>
                          <Box sx={{ 
                            width: 32, 
                            height: 32, 
                            borderRadius: '10px', 
                            background: 'rgba(0, 212, 255, 0.1)', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            mr: 2,
                            color: '#00d4ff'
                          }}>
                            <Person sx={{ fontSize: 18 }} />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.userName}</Typography>
                        </Box>

                        {/* Desktop Sessions Info */}
                        {userSessions[user.userName] && userSessions[user.userName].length > 0 && (
                          <Box sx={{ ml: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box 
                              onClick={() => toggleExpand(user.userName)}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                pr: 2,
                                '&:hover .MuiTypography-root': { color: 'rgba(255, 255, 255, 0.6)' }
                              }}
                            >
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px', transition: 'color 0.2s' }}>
                                {expandedUsers[user.userName] ? 'Recent Sessions' : 'Last Session'}
                              </Typography>
                              {userSessions[user.userName].length > 1 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.3)' }}>
                                  {expandedUsers[user.userName] ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                </Box>
                              )}
                            </Box>
                            
                            {(expandedUsers[user.userName] 
                              ? userSessions[user.userName] 
                              : [userSessions[user.userName][0]]
                            ).map((session, idx) => {
                              const isOnline = (Date.now() - new Date(session.updatedAt)) < 10 * 60 * 1000;
                              const deviceName = session.appVersion 
                                ? `${session.deviceOS} (App v${session.appVersion})`
                                : `${session.deviceOS} (${session.deviceType})`;
                                
                              return (
                                <SessionInfo key={idx} onClick={() => toggleExpand(user.userName)}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <StatusBadge online={isOnline}>
                                      {isOnline ? 'ONLINE' : 'Offline'}
                                    </StatusBadge>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.65rem' }}>
                                      {new Date(session.updatedAt).toLocaleString()}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, fontSize: '0.7rem' }}>
                                    {deviceName}
                                  </Typography>
                                </SessionInfo>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{user.photos.toLocaleString()}</TableCell>
                    <TableCell align="right">{user.videos.toLocaleString()}</TableCell>
                    <TableCell align="right">{formatBytes(user.usage)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#00ff88', minWidth: '45px' }}>
                          {share}%
                        </Typography>
                        <Box sx={{ width: 100 }}>
                          <ShareBar share={share} color={getShareColor(share)} />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}
    </Box>
  );
};

export default ImmichStatistics;
